/**
 * Namespaced console logger — the developer-facing half of `src/logging`.
 *
 * Two audiences share this module's roof deliberately. Console output is for
 * a person reading devtools; the event pipeline is for a dashboard. Keeping
 * them in one module is what stops the codebase drifting back into "there
 * are three logging utilities and nobody is sure which to call".
 *
 * ## Levels
 *
 * - `log` / `info` — development only. Verbose by design, absent in prod.
 * - `warn` — always printed, never emitted as an event. Warnings are for
 *   the person with devtools open; promoting them to the error charts would
 *   bury real failures in noise.
 * - `error` — always printed, **and** forwarded to the telemetry pipeline as
 *   a `web_error` event and to Cloud Error Reporting.
 *
 * ## Why the sink is late-bound
 *
 * This module has no import of the telemetry pipeline. It cannot: the
 * pipeline reaches Firebase App Check, and `firebase-config.ts` logs through
 * this module, so a direct import would close an import cycle. Cycles here
 * would technically work today and break silently the first time someone
 * read a value at module scope instead of inside a function.
 *
 * Instead `telemetry.ts` registers itself here when it loads. The bounded
 * consequence, stated rather than hidden: an error logged *before* the
 * telemetry module has loaded reaches the console but not the dashboard.
 * `main.tsx` imports `@logging` first to keep that window as small as it can
 * be made.
 *
 * @module logging/console-logger
 */

import { describeError } from './core/classify';

// =============================================================================
// LATE-BOUND ERROR SINK
// =============================================================================

/** Receives errors that should travel beyond the console. */
export type ErrorSink = (namespace: string, message: string, cause: unknown) => void;

let errorSink: ErrorSink | null = null;

/**
 * Register the telemetry pipeline as the destination for logged errors.
 * Called once, by `telemetry.ts`, on load. Not part of the public API.
 */
export function setErrorSink(sink: ErrorSink): void {
  errorSink = sink;
}

// =============================================================================
// LOGGER
// =============================================================================

export interface Logger {
  /** Debug detail. Development only. */
  log: (...args: unknown[]) => void;
  /** Debug detail. Development only. */
  info: (...args: unknown[]) => void;
  /** Always printed. Console only — never becomes an event. */
  warn: (...args: unknown[]) => void;
  /** Always printed, and reported to the dashboards and Error Reporting. */
  error: (...args: unknown[]) => void;
}

const isDev = import.meta.env.DEV;

/** Join mixed console arguments into one line for the event payload. The
 *  console still receives the original arguments, so devtools keeps its
 *  expandable objects. */
function summarize(args: readonly unknown[]): string {
  return args.map(describeError).join(' ').trim() || 'unknown_error';
}

/** The first real `Error` in the arguments, whose stack is what Cloud Error
 *  Reporting groups on. */
function findCause(args: readonly unknown[]): unknown {
  return args.find((arg) => arg instanceof Error) ?? args[0];
}

/**
 * Create a namespaced logger.
 *
 * @param namespace - Prefix for console output and the `location` recorded
 *                    on any error event, e.g. 'Firebase', 'ContactUsPage'.
 */
export function createLogger(namespace: string): Logger {
  const prefix = `[${namespace}]`;

  return {
    log: (...args: unknown[]) => {
      if (isDev) console.log(prefix, ...args);
    },
    info: (...args: unknown[]) => {
      if (isDev) console.info(prefix, ...args);
    },
    warn: (...args: unknown[]) => {
      console.warn(prefix, ...args);
    },
    error: (...args: unknown[]) => {
      console.error(prefix, ...args);
      // Reporting must never turn one failure into two.
      try {
        errorSink?.(namespace, summarize(args), findCause(args));
      } catch {
        // Deliberately silent: this is already the error path.
      }
    },
  };
}

/** Unnamespaced logger for one-off use. Prefer `createLogger`. */
export const logger: Logger = createLogger('app');
